import { useMemo, useState } from "react"
import Papa from "papaparse"
import { FileSpreadsheet, Copy, Check, ArrowUp, ArrowDown } from "lucide-react"

interface CsvTableProps {
  filePath: string
  rawContent: string
}

export function CsvTable({ filePath, rawContent }: CsvTableProps) {
  const [showCopiedToast, setShowCopiedToast] = useState(false)
  const [sortColumn, setSortColumn] = useState<number | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

  const parsed = useMemo(() => {
    return Papa.parse<string[]>(rawContent, {
      skipEmptyLines: true,
    })
  }, [rawContent])

  const headers = parsed.data[0] || []
  const rows = parsed.data.slice(1)

  const sortedRows = useMemo(() => {
    if (sortColumn === null) return rows
    return [...rows].sort((a, b) => {
      const aVal = a[sortColumn] || ""
      const bVal = b[sortColumn] || ""
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true })
      return sortDirection === "asc" ? cmp : -cmp
    })
  }, [rows, sortColumn, sortDirection])

  function handleSort(colIndex: number) {
    if (sortColumn === colIndex) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortColumn(colIndex)
      setSortDirection("asc")
    }
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(rawContent)
    setShowCopiedToast(true)
    setTimeout(() => setShowCopiedToast(false), 2000)
  }

  const pathParts = filePath.split("/").filter(Boolean)
  const fileName = pathParts.pop() || "Untitled"
  const folderPath = pathParts

  const rowCount = rows.length
  const colCount = headers.length

  return (
    <div className="min-h-[calc(100vh-3.5rem)]">
      {/* File info */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 pb-2">
        <div>
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet
              className="w-4 h-4 text-muted-foreground/50 flex-shrink-0"
              strokeWidth={1.5}
            />
            <p className="text-sm font-medium text-foreground truncate min-w-0 flex-1">
              {fileName}
            </p>
            <span className="text-xs text-muted-foreground shrink-0">
              {rowCount} {rowCount === 1 ? "row" : "rows"}, {colCount} {colCount === 1 ? "column" : "columns"}
            </span>
          </div>
          {folderPath.length > 0 && (
            <p className="text-[11px] text-muted-foreground truncate mt-1 ml-6">
              {folderPath.map((seg, idx) => (
                <span key={idx}>
                  {idx > 0 && (
                    <span className="mx-1 text-muted-foreground/50">/</span>
                  )}
                  <span>{seg}</span>
                </span>
              ))}
            </p>
          )}
        </div>
      </div>

      {/* Parse errors warning */}
      {parsed.errors.length > 0 && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-4">
          <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
            Some rows could not be parsed. The file may have formatting issues.
          </div>
        </div>
      )}

      {/* Table */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 pb-32">
        {rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            This CSV file is empty
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-card shadow-xs">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted">
                  {headers.map((header, i) => (
                    <th
                      key={i}
                      className="sticky top-0 bg-muted text-left px-4 py-2.5 font-medium text-foreground border-b whitespace-nowrap cursor-pointer select-none hover:bg-accent"
                      onClick={() => handleSort(i)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {header}
                        {sortColumn === i && (
                          sortDirection === "asc"
                            ? <ArrowUp className="h-3 w-3" />
                            : <ArrowDown className="h-3 w-3" />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, rowIdx) => (
                  <tr
                    key={rowIdx}
                    className="border-b even:bg-muted/30 hover:bg-accent/50"
                  >
                    {headers.map((_, colIdx) => (
                      <td
                        key={colIdx}
                        className="px-4 py-2 text-foreground whitespace-nowrap"
                      >
                        {row[colIdx] || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Floating controls */}
      <div className="fixed bottom-24 right-4 md:bottom-6 md:right-8 z-30 flex items-center gap-3">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Read-only
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-card border shadow-xs text-sm text-muted-foreground hover:text-primary hover:border-ring transition-all"
        >
          <Copy className="w-4 h-4" strokeWidth={1.5} />
          <span className="hidden sm:inline font-medium">Copy</span>
        </button>
      </div>

      {/* Copied toast */}
      <div
        className={`fixed bottom-36 right-4 md:bottom-[4.5rem] md:right-8 z-40 transition-all duration-200 ${
          showCopiedToast
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-foreground text-background text-xs font-medium shadow-lg">
          <Check className="w-3 h-3" strokeWidth={2.5} />
          Copied!
        </div>
      </div>
    </div>
  )
}
